import { apiClient } from './client'
import type { ApiResponse } from './client'
import type {
  SendingDomainViewDto,
  SendingDomainCreateDto,
  ConfigureTrackingDto,
  SendingDomainDeveloperInstructionsDto,
  SendingDomainWarmupViewDto,
  SendingDomainWarmupSummaryDto,
  SendingDomainForecastRequestDto,
  SendingDomainForecastViewDto,
} from '@/types/sendingDomains'

export const sendingDomainsApi = {
  list: (clientCompanyId: string): Promise<ApiResponse<SendingDomainViewDto[]>> =>
    apiClient.get(`/api/ClientCompanies/${clientCompanyId}/SendingDomains`),

  get: (clientCompanyId: string, id: string): Promise<ApiResponse<SendingDomainViewDto>> =>
    apiClient.get(`/api/ClientCompanies/${clientCompanyId}/SendingDomains/${id}`),

  create: (clientCompanyId: string, dto: SendingDomainCreateDto): Promise<ApiResponse<SendingDomainViewDto>> =>
    apiClient.post(`/api/ClientCompanies/${clientCompanyId}/SendingDomains`, dto),

  remove: (clientCompanyId: string, id: string): Promise<ApiResponse<null>> =>
    apiClient.delete(`/api/ClientCompanies/${clientCompanyId}/SendingDomains/${id}`),

  // Triggers a DNS re-check in Resend and returns the refreshed status + records.
  verify: (clientCompanyId: string, id: string): Promise<ApiResponse<SendingDomainViewDto>> =>
    apiClient.post(`/api/ClientCompanies/${clientCompanyId}/SendingDomains/${id}/Verify`, {}),

  // Read-through status refresh (re-syncs from Resend without re-triggering verification).
  status: (clientCompanyId: string, id: string): Promise<ApiResponse<SendingDomainViewDto>> =>
    apiClient.get(`/api/ClientCompanies/${clientCompanyId}/SendingDomains/${id}/Status`),

  // Configures open/click tracking (and an optional custom tracking subdomain); returns the
  // refreshed domain, whose DnsRecords now include the generated tracking CNAME.
  configureTracking: (clientCompanyId: string, id: string, dto: ConfigureTrackingDto): Promise<ApiResponse<SendingDomainViewDto>> =>
    apiClient.post(`/api/ClientCompanies/${clientCompanyId}/SendingDomains/${id}/Tracking`, dto),

  // Marks this domain as the company default (auto-selected for activations + test sends). The target
  // must be Verified; the previous default is cleared server-side. Returns the updated domain.
  setDefault: (clientCompanyId: string, id: string): Promise<ApiResponse<SendingDomainViewDto>> =>
    apiClient.post(`/api/ClientCompanies/${clientCompanyId}/SendingDomains/${id}/Default`, {}),

  // Warm-up (ORB-953). Both are company-admin actions; the screens confirm before calling them.
  restartWarmup: (clientCompanyId: string, id: string): Promise<ApiResponse<SendingDomainViewDto>> =>
    apiClient.post(`/api/ClientCompanies/${clientCompanyId}/SendingDomains/${id}/RestartWarmup`, {}),

  resume: (clientCompanyId: string, id: string): Promise<ApiResponse<SendingDomainViewDto>> =>
    apiClient.post(`/api/ClientCompanies/${clientCompanyId}/SendingDomains/${id}/Resume`, {}),

  // Emails what is still to do on a setting-up domain to whoever manages the company's DNS.
  sendDeveloperInstructions: (
    clientCompanyId: string,
    id: string,
    dto: SendingDomainDeveloperInstructionsDto,
  ): Promise<ApiResponse<SendingDomainViewDto>> =>
    apiClient.post(`/api/ClientCompanies/${clientCompanyId}/SendingDomains/${id}/DeveloperInstructions`, dto),

  // One domain's day, last three days and step ladder, worked out on the server.
  warmup: (clientCompanyId: string, id: string): Promise<ApiResponse<SendingDomainWarmupViewDto>> =>
    apiClient.get(`/api/ClientCompanies/${clientCompanyId}/SendingDomains/${id}/Warmup`),

  // Today's sends and held emails for every domain of the company, for the lists.
  warmupSummary: (clientCompanyId: string): Promise<ApiResponse<SendingDomainWarmupSummaryDto>> =>
    apiClient.get(`/api/ClientCompanies/${clientCompanyId}/SendingDomainWarmup`),

  // How the chosen domain would carry the activation editor's unsaved form. Reads the whole audience,
  // so call it when the domain, the rule or the lists change, never per keystroke.
  forecast: (
    workspaceId: string,
    webId: string,
    dto: SendingDomainForecastRequestDto,
  ): Promise<ApiResponse<SendingDomainForecastViewDto>> =>
    apiClient.post(`/api/Workspaces/${workspaceId}/Webs/${webId}/SendingDomainForecast`, dto),
}
